/**
 * Blöcke im Beleg — Überschrift, Positionen darunter, Zwischensumme.
 *
 * Die Zeilenliste bleibt **flach** (`draft.line_rows`); ein Block ist eine
 * Zeile mit `line_kind: "section"`, und er klammert alles, was ihm folgt, bis
 * zur nächsten Blockzeile oder zum Listenende. Genau so liest es die
 * PDF-Engine (`render_pdf._flush_section_subtotal`) — die Gruppierung hier ist
 * ihr Spiegelbild, damit der Editor keine andere Zwischensumme zeigt als das
 * fertige Dokument.
 *
 * Warum der gespeicherte Wert `"section"` heißt, obwohl die Oberfläche „Block"
 * sagt: festgeschriebene Belege behalten ihr `web_document_json`, das PDF
 * liegt beim Kunden. Ein zweiter Wert mit gleicher Bedeutung wäre eine
 * Altlast ab Tag eins — die Engine rendert Überschrift und Zwischensumme
 * längst richtig.
 */
import type { CatalogBlock, CatalogPosition, WebInvoiceLine } from "./invoices";
import { newInvoiceLine } from "./invoiceEditor";

/** Der gespeicherte `line_kind` eines Blocks. In der Oberfläche: „Block". */
export const BLOCK_KIND = "section" as const;

export const istBlock = (line: WebInvoiceLine) => line.line_kind === BLOCK_KIND;

/** Eine Zeile samt ihrer Stelle in der flachen Liste. */
export type ZeileMitIndex = { line: WebInvoiceLine; index: number };

/**
 * Ein Block mit seinen Positionen — oder die freien Positionen am Anfang,
 * dann ist `block` null.
 */
export type Zeilengruppe = {
  block: WebInvoiceLine | null;
  /** Stelle der Blockzeile in `line_rows`; -1 bei freien Positionen. */
  blockIndex: number;
  zeilen: ZeileMitIndex[];
};

/**
 * Die flache Liste in Gruppen zerlegen.
 *
 * Positionen vor dem ersten Block bleiben frei — ein kurzes Angebot mit drei
 * Zeilen braucht keine Überschrift. Nach einem Block gehört alles zu ihm,
 * so wie eine Überschrift in einem Dokument gilt, bis die nächste kommt.
 */
export function gruppiereZeilen(lines: WebInvoiceLine[]): Zeilengruppe[] {
  const gruppen: Zeilengruppe[] = [];
  let aktuell: Zeilengruppe | null = null;
  lines.forEach((line, index) => {
    if (istBlock(line)) {
      aktuell = { block: line, blockIndex: index, zeilen: [] };
      gruppen.push(aktuell);
      return;
    }
    if (!aktuell) {
      aktuell = { block: null, blockIndex: -1, zeilen: [] };
      gruppen.push(aktuell);
    }
    aktuell.zeilen.push({ line, index });
  });
  return gruppen;
}

const zeilenNetto = (gruppe: Zeilengruppe, kind: WebInvoiceLine["line_kind"]): number => {
  const summe = gruppe.zeilen.reduce((acc, { line }) => (
    line.line_kind === kind
      ? acc + (Number(line.quantity) || 0) * (Number(line.unit_price_net) || 0)
      : acc
  ), 0);
  return Math.round((summe + Number.EPSILON) * 100) / 100;
};

/**
 * Netto einer Gruppe. Optionale Positionen zählen NICHT — genau wie in der
 * Gesamtsumme und im PDF; sonst verspräche die Zwischensumme einen Betrag,
 * den der Kunde gar nicht beauftragt hat.
 */
export function gruppeNetto(gruppe: Zeilengruppe): number {
  return zeilenNetto(gruppe, "position");
}

/**
 * Netto der optionalen Positionen einer Gruppe — die Zahl, die im Editor und
 * im PDF als eigene Zeile UNTER der Zwischensumme steht (Benjamin,
 * 15.09.2026). Sie ist eine Auskunft, keine Forderung: addiert wird sie
 * nirgends dazu, aber der Kunde musste sie vorher im Kopf zusammenzählen.
 */
export function gruppeOptionalNetto(gruppe: Zeilengruppe): number {
  return zeilenNetto(gruppe, "optional");
}

/** Laufende Positionsnummer wie im PDF — Blockzeilen zählen nicht mit. */
export function positionsNummer(lines: WebInvoiceLine[], index: number): number {
  return lines.slice(0, index + 1).filter((line) => !istBlock(line)).length;
}

/** Alle Zeilen einer Gruppe (Blockkopf eingeschlossen) als zusammenhängender Ausschnitt. */
function gruppenSpanne(lines: WebInvoiceLine[], blockIndex: number): [number, number] {
  let ende = blockIndex + 1;
  while (ende < lines.length && !istBlock(lines[ende])) ende += 1;
  return [blockIndex, ende];
}

/**
 * Einen Block mitsamt seinen Positionen verschieben.
 *
 * Der eigentliche Grund für diese Funktion: mit den Zeilenpfeilen war ein
 * Block aus acht Positionen nur in acht mal acht Klicks umzustellen.
 * Verschoben wird über den benachbarten Block hinweg, nicht um eine Zeile.
 */
export function verschiebeBlock(
  lines: WebInvoiceLine[],
  blockIndex: number,
  richtung: -1 | 1,
): WebInvoiceLine[] {
  const gruppen = gruppiereZeilen(lines);
  const stelle = gruppen.findIndex((gruppe) => gruppe.blockIndex === blockIndex);
  const ziel = stelle + richtung;
  if (stelle < 0 || !gruppen[stelle].block) return lines;
  if (ziel < 0 || ziel >= gruppen.length) return lines;
  // Nicht über die freien Positionen hinweg: die stehen immer vorn, und ein
  // Block davor würde sie beim nächsten Lesen in sich aufnehmen.
  if (!gruppen[ziel].block) return lines;
  const neu = [...gruppen];
  [neu[stelle], neu[ziel]] = [neu[ziel], neu[stelle]];
  return flachlegen(neu);
}

/** Gruppen zurück in die flache Zeilenliste. */
function flachlegen(gruppen: Zeilengruppe[]): WebInvoiceLine[] {
  return gruppen.flatMap((gruppe) => {
    const zeilen = gruppe.zeilen.map(({ line }) => line);
    return gruppe.block ? [gruppe.block, ...zeilen] : zeilen;
  });
}

/** Ob sich der Block in diese Richtung überhaupt bewegen lässt. */
export function blockVerschiebbar(
  lines: WebInvoiceLine[],
  blockIndex: number,
  richtung: -1 | 1,
): boolean {
  return verschiebeBlock(lines, blockIndex, richtung) !== lines;
}

/** Block auflösen: die Überschrift geht, die Positionen bleiben stehen. */
export function loeseBlockAuf(lines: WebInvoiceLine[], blockIndex: number): WebInvoiceLine[] {
  if (!istBlock(lines[blockIndex])) return lines;
  return lines.filter((_, index) => index !== blockIndex);
}

/** Block mitsamt Positionen entfernen. */
export function entferneBlock(lines: WebInvoiceLine[], blockIndex: number): WebInvoiceLine[] {
  if (!istBlock(lines[blockIndex])) return lines;
  const [start, ende] = gruppenSpanne(lines, blockIndex);
  return [...lines.slice(0, start), ...lines.slice(ende)];
}

/** Block mitsamt Positionen kopieren — direkt dahinter, mit frischen IDs. */
export function dupliziereBlock(lines: WebInvoiceLine[], blockIndex: number): WebInvoiceLine[] {
  if (!istBlock(lines[blockIndex])) return lines;
  const [start, ende] = gruppenSpanne(lines, blockIndex);
  const kopie = lines.slice(start, ende).map((line) => ({ ...line, id: newInvoiceLine().id }));
  return [...lines.slice(0, ende), ...kopie, ...lines.slice(ende)];
}

const vatWert = (value: number | string): 0 | 7 | 19 => {
  const zahl = Number(value);
  return zahl === 0 || zahl === 7 ? zahl : 19;
};

/** Eine Katalogposition als Belegzeile. */
export function zeileAusKatalog(
  item: CatalogPosition,
  kind: WebInvoiceLine["line_kind"] = "position",
): WebInvoiceLine {
  return {
    ...newInvoiceLine(kind),
    title: (item.title || "").trim(),
    description: (item.description || "").trim(),
    unit_label: item.unit_label || "Stück",
    unit_code: item.unit_code || "C62",
    unit_price_net: Number(item.unit_price_net) || 0,
    vat_rate_percent: vatWert(item.vat_rate_percent),
    // Pflanze aus der Bibliothek: woraus der Preis kam, damit er sich je Zeile nachrechnen lässt.
    ...(item.pflanze_id ? {
      pflanze_id: item.pflanze_id,
      einkauf_netto: item.einkauf_netto ?? null,
      aufschlag: item.aufschlag ?? null,
    } : {}),
  };
}

/** Einen Baustein der Bibliothek in Belegzeilen übersetzen: Kopf plus Positionen. */
export function zeilenAusKatalogBlock(block: CatalogBlock): WebInvoiceLine[] {
  const kopf = newInvoiceLine(BLOCK_KIND);
  return [
    { ...kopf, title: (block.title || "").trim(), description: (block.description || "").trim() },
    ...(block.lines || []).map((line) => zeileAusKatalog(line)),
  ];
}

/**
 * Einen Baustein in einen **bestehenden** Block übernehmen.
 *
 * Gegenstück zu `zeilenAusKatalogBlock`, das einen eigenen Blockkopf
 * mitbringt: hier steht der Kopf schon, und ein zweiter darunter würde den
 * Block in zwei zerlegen. Überschrift und Zusatztext wandern deshalb in den
 * vorhandenen Kopf, die Positionen werden an das Ende dieses Blocks gehängt —
 * **angehängt, nicht ersetzend**, wie beim Anwenden einer Vorlage, sonst wäre
 * ein versehentlich gewählter Baustein nicht rückgängig zu machen. Eine
 * unberührte leere Startposition ist Platzhalter, kein Inhalt, und darf weg.
 *
 * Ein leerer Zusatztext im Baustein lässt den vorhandenen stehen: der Nutzer
 * hat ihn getippt, die Bibliothek weiß nichts davon.
 */
export function setzeBausteinInBlock(
  lines: WebInvoiceLine[],
  blockIndex: number,
  block: CatalogBlock,
): WebInvoiceLine[] {
  const kopf = lines[blockIndex];
  if (!kopf || !istBlock(kopf)) return lines;
  let ende = blockIndex + 1;
  while (ende < lines.length && !istBlock(lines[ende])) ende += 1;
  const bestehende = lines.slice(blockIndex + 1, ende).filter(
    (zeile) => zeile.title.trim() || zeile.description.trim() || Number(zeile.unit_price_net) !== 0,
  );
  const neue = zeilenAusKatalogBlock(block).filter((zeile) => !istBlock(zeile));
  return [
    ...lines.slice(0, blockIndex),
    {
      ...kopf,
      title: (block.title || "").trim() || kopf.title,
      description: (block.description || "").trim() || kopf.description,
    },
    ...bestehende,
    ...neue,
    ...lines.slice(ende),
  ];
}

/**
 * Einen Block aus dem Beleg für die Bibliothek aufbereiten.
 *
 * Mengen wandern bewusst NICHT mit: ein Baustein trägt die Leistungen, die
 * Menge hängt am einzelnen Auftrag. Optionale Positionen kommen als normale
 * Positionen mit — ob etwas optional ist, entscheidet sich am Angebot.
 */
export function katalogBlockAusGruppe(gruppe: Zeilengruppe): CatalogBlock {
  return {
    title: (gruppe.block?.title || "").trim(),
    description: (gruppe.block?.description || "").trim(),
    lines: gruppe.zeilen.map(({ line }) => ({
      title: line.title.trim(),
      description: line.description.trim(),
      unit_label: line.unit_label || "Stück",
      unit_code: line.unit_code || "C62",
      unit_price_net: Number(line.unit_price_net) || 0,
      vat_rate_percent: line.vat_rate_percent,
    })),
  };
}

/**
 * Gewählte Positionen zu einem neuen Block zusammenfassen.
 *
 * Der neue Block entsteht dort, wo die Gruppe der ersten gewählten Position
 * **endet** — nicht an ihrer Stelle: eine Blockzeile klammert alles, was ihr
 * folgt, und mitten in einem bestehenden Block eingesetzt würde sie dessen
 * restliche Positionen stillschweigend übernehmen. Gewählte Blöcke selbst
 * bleiben unberührt; ohne gewählte Position passiert nichts.
 */
export function fasseZuBlockZusammen(
  lines: WebInvoiceLine[],
  gewaehlt: Set<string>,
): { lines: WebInvoiceLine[]; block: WebInvoiceLine } | null {
  const ausgewaehlt = lines.filter((line) => !istBlock(line) && gewaehlt.has(line.id));
  if (ausgewaehlt.length === 0) return null;

  const ersteStelle = lines.findIndex((line) => line.id === ausgewaehlt[0].id);
  let gruppenEnde = ersteStelle + 1;
  while (gruppenEnde < lines.length && !istBlock(lines[gruppenEnde])) gruppenEnde += 1;

  const davorEntfernt = ausgewaehlt.filter((line) => lines.indexOf(line) < gruppenEnde).length;
  const rest = lines.filter((line) => !ausgewaehlt.includes(line));
  const einfuegen = gruppenEnde - davorEntfernt;
  const block = newInvoiceLine(BLOCK_KIND);
  return {
    lines: [...rest.slice(0, einfuegen), block, ...ausgewaehlt, ...rest.slice(einfuegen)],
    block,
  };
}
